import dayjs from "lib/dayjs";
import { createCompassPdf } from "reporting/pdf/createCompassPdf";
import { fmtDate, safeFilePart } from "reporting/pdf/pdfFormatters";
import { buildPortfolioMainReportData } from "pages/FundPlanner/components/Portfolios/Main/portfolioMainReportData";
import { buildPortfolioPositionsReportData } from "./buildPortfolioPositionsReportData";
import { renderPortfolioMainPdf } from "./exportPortfolioMainPdf";
import { renderPortfolioPositionsPdf } from "./exportPortfolioPositionsPdf";
import { renderPortfolioCashFlowsPdf } from "./exportPortfolioCashFlowsPdf";
import { getPortfolioPeriodPdfLabel } from "./portfolioPeriodPdfLabels";
import { renderFundPlannerDisclosurePdf } from "./renderFundPlannerDisclosurePdf";

function mainAsOfLabel(data) {
  if (data.meta.reportMode === "period" && data.periodOverview) {
    return getPortfolioPeriodPdfLabel(data.meta.startDate, data.meta.endDate);
  }
  return data.meta.asOfDate ? `As of ${fmtDate(data.meta.asOfDate)}` : "";
}

export default async function exportPortfolioBundlePdf({
  orgId = null,
  selectedReports = [],
  reportArgs = {},
  cashFlowArgs = {},
  save = true,
} = {}) {
  const sections = [];

  if (selectedReports.includes("overview")) {
    const data = buildPortfolioMainReportData(reportArgs);
    sections.push({
      key: "overview",
      title: data.meta.reportTitle,
      orgName: data.meta.orgName || data.meta.portfolioName,
      asOfLabel: mainAsOfLabel(data),
      orientation: "portrait",
      contentMargins: {},
      render: (ctx) => renderPortfolioMainPdf(ctx, data),
    });
  }

  if (selectedReports.includes("positions")) {
    const data = buildPortfolioPositionsReportData(reportArgs);
    sections.push({
      key: "positions",
      title: data.meta.reportTitle,
      orgName: data.meta.orgName || data.meta.portfolioName,
      asOfLabel: data.meta.asOfLabel,
      orientation: "landscape",
      contentMargins: { top: 102, bottom: 58 },
      render: (ctx) => renderPortfolioPositionsPdf(ctx, data),
    });
  }

  if (selectedReports.includes("cashFlows")) {
    sections.push({
      key: "cashFlows",
      title: "FundPlanner - Portfolio Cash Flows",
      orgName: cashFlowArgs.orgName || cashFlowArgs.scopeName || cashFlowArgs.portfolioName,
      asOfLabel: cashFlowArgs.asOfDate ? `As of ${fmtDate(cashFlowArgs.asOfDate)}` : "",
      orientation: "portrait",
      contentMargins: { top: 102, bottom: 58 },
      render: (ctx) => renderPortfolioCashFlowsPdf(ctx, cashFlowArgs),
    });
  }

  if (!sections.length) throw new Error("Select at least one report to export.");

  const first = sections[0];
  const ctx = await createCompassPdf({
    orgId,
    reportTitle: first.title,
    orgName: first.orgName,
    asOfLabel: first.asOfLabel,
    createdStamp: new Date(),
    orientation: first.orientation,
    branding: "unbranded",
    contentMargins: first.contentMargins,
  });

  sections.forEach((section, index) => {
    ctx.startSection({
      reportTitle: section.title,
      orgName: section.orgName,
      asOfLabel: section.asOfLabel,
      orientation: section.orientation,
      contentMargins: section.contentMargins,
      addPage: index > 0,
    });
    section.render(ctx);
  });

  renderFundPlannerDisclosurePdf(ctx);

  ctx.finalize();

  const fileName = `FundPlanner_Portfolio_${safeFilePart(
    reportArgs.orgName || reportArgs.portfolioName || "Portfolio"
  )}_${safeFilePart(reportArgs.portfolioName || "Reports")}_${dayjs(
    reportArgs.endDate || reportArgs.asOfDate || new Date()
  ).format("YYYY-MM-DD")}.pdf`;

  if (save) ctx.doc.save(fileName);

  return { doc: ctx.doc, fileName, sections: sections.map((section) => section.key) };
}
